import { useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { REFERRAL_OPEN_FRIEND_PARAM } from '@/components/features/referral/model/constants';
import Toast from '@/components/ui/Toast/toast';
import { createParsedEventHandler } from '@/hooks/socket/socket.helpers';
import { useSocketEvent } from '@/hooks/socket/useSocketEvent';
import { AppRoutes } from '@/i18n/constants';
import { useRouter } from '@/i18n/routing';
import { useAppSelector } from '@/redux/hooks';
import { invalidateRefereeRewardState } from './referral.queries';
import { RefereeRegisteredContextSchema, RefereeStatusUpContextSchema } from './referral.schema';

export const REFERRAL_SOCKET_EVENTS = {
  refereeRegistered: 'referral.referee.registered',
  refereeStatusUp: 'referral.referee.status-up',
} as const;

export function useReferralSocket() {
  const queryClient = useQueryClient();
  const userSession = useAppSelector(state => state.user.userSession);
  const isSessionActive = Boolean(userSession);
  const t = useTranslations('referral.toasts');
  const router = useRouter();

  // The toast id keeps one toast per friend (and per status), so a repeated event does not stack toasts.
  const showReferralToast = (
    toastKey: 'friend-registered' | 'friend-status-up',
    refereeUserId: string,
    toastId = refereeUserId,
  ) => {
    Toast({
      id: `referral-${toastKey}-${toastId}`,
      type: 'success',
      title: t(`${toastKey}.title`),
      description: t(`${toastKey}.description`),
      acceptButtonText: t(`${toastKey}.action`),
      rejectButtonText: t('later'),
      // The referral page sorts friends by the latest update and opens this friend's card.
      onAccept: () => router.push(`${AppRoutes.referral}?${REFERRAL_OPEN_FRIEND_PARAM}=${refereeUserId}`),
    });
  };

  const handleRefereeRegistered = createParsedEventHandler(RefereeRegisteredContextSchema, (data) => {
    invalidateRefereeRewardState(queryClient);
    showReferralToast('friend-registered', data.refereeUserId);
  });

  const handleRefereeStatusUp = createParsedEventHandler(RefereeStatusUpContextSchema, (data) => {
    invalidateRefereeRewardState(queryClient);
    showReferralToast('friend-status-up', data.refereeUserId, `${data.refereeUserId}`);
  });

  useSocketEvent(
    REFERRAL_SOCKET_EVENTS.refereeRegistered,
    handleRefereeRegistered,
    isSessionActive,
  );

  useSocketEvent(
    REFERRAL_SOCKET_EVENTS.refereeStatusUp,
    handleRefereeStatusUp,
    isSessionActive,
  );
}
